/**
 * Thin domain-friendly wrappers over auto-generated OpenAPI SDK functions.
 *
 * Why:
 *  - Keep ergonomic names (createClient, listClients, etc.)
 *  - Centralize auth header building & future cross-cutting concerns (retry, logging)
 *  - Decouple the rest of the app from verbose generated identifiers so regeneration is painless
 *  - Provide stable return shapes matching existing manual API functions
 *
 * Migration:
 *  1. Start replacing imports from '@/lib/apis' with '@/lib/apiSdk'.
 *  2. After all usages move, delete or shrink the old `apis.ts` (can re-export these wrappers).
 *  3. Regenerate with `npm run openapi-ts` whenever the backend spec changes; wrappers usually stay.
 */

import {
  deleteAccountData,
  deleteEventByClientIdByEventIdVersionsByVersionId,
  deleteEventByEventId,
  deleteOrg,
  deleteOrgInvitesByInviteId,
  deleteOrgMembersByUserId,
  deleteTemplatesByTemplateId,
  getAuthMe,
  getAuthMfaStatus,
  getClientAccessList,
  getClientConsentGet,
  getClientGet,
  getClientList,
  getClientTransfersIncoming,
  getClientTransfersOutgoing,
  getEventsByClientIdByEventId,
  getEventsByClientIdByEventIdVersions,
  getHealth,
  getOrgInvites,
  getOrgList,
  getOrgMembers,
  getOrgMembersByUserId,
  getStripeCheckSubscriptionStatus,
  getUsageSummary as getUsageSummarySdk,
  getStripeInvoices,
  getSupportThreads,
  getSupportThreadsByThreadId,
  getTemplatesByTemplateId,
  getTemplatesList,
  getUser as getUserApi,
  getUserInvites as getUserInvitesApi,
  getUserTotpStatus,
  getUserTransfers as getUserTransfersApi,
  patchOrgMembersByUserIdRole,
  patchOrgName,
  patchTemplatesByTemplateId,
  patchTranscriptionSpeakerLabel as patchTranscriptionSpeakerLabelApi,
  patchTranscriptionText as patchTranscriptionTextApi,
  patchUser,
  postAuthMfaSend,
  postAuthMfaVerify,
  postAuthPasswordForgot,
  postAuthPasswordReset,
  postAuthRegister,
  postAuthRevokeSessions,
  postClientAccessGrant,
  postClientAccessRevoke,
  postClientCheckOwnership,
  postClientConsentRequest,
  postClientConsentSubmit,
  postClientCreate,
  postClientPortalInvite,
  postClientTransfersInitiate,
  postClientUpdate,
  postEventsAutosave,
  postEventsBatch,
  postEventsCommitVersion,
  postEventsCreate,
  postEventsUpdateReferences,
  postEventsUpdateTemplate,
  postEventsUpdateTitle,
  postOrg,
  postOrgActive,
  postOrgInvites,
  postOrgInvitesByInviteIdAccept,
  postOrgInvitesByInviteIdReject,
  postOrgInvitesByInviteIdResend,
  postSessionTriggerAiSummary,
  postStripeConfirmSubscription,
  postStripeCreateSubscriptionIntent,
  postStripeCustomerPortal,
  postSupportThreadsByThreadIdClose,
  postTemplatesCreate,
  getTranscriptionGet,
  postTranscriptionMockComplete,
  postTranscriptionStart,
  postTranscriptionUpload,
  postUserConfirmEmailChange,
  postUserConfirmTotpSetup,
  postUserDisableTotp,
  postUserRequestEmailChange,
  postUserSetupTotp,
  postUserVerifyTotp,
} from "@/client";

import type {
  AuthMeResponse,
  ClientSummary,
  ClientTransferItem,
  ConfirmEmailChangeBody,
  CreateClientRequest,
  CreateTemplateRequest,
  InvoicesResponse,
  MessageDoc,
  OrgInvitation,
  OrgListEntry,
  OrgMemberWithEmail,
  RequestEmailChangeBody,
  RequestEmailChangeResponse,
  SetupTotpResponse,
  ThreadDetailResponse,
  ThreadsListResponse,
  TotpStatusResponse,
  TransferListResponse,
  MfaSendResponse,
  MfaStatusResponse,
  MfaVerifyResponse,
  RegisterResponse,
  UpdateClientRequest,
  UpdateTemplateMetaRequest,
  UpdateUserRequest,
  User,
  UserInviteItem,
  UserInviteListResponse,
  UserTransferListResponse,
} from "@/client";

export type CreateOrgResponse = {
  org_id: string;
  name: string;
};

import { authedClient, withAuthHeaders } from "@/lib/apiClient";

import { auth } from "@/lib/firebase/client";

// Unified execution helper to reduce repetition
type SdkResult<T> = {
  data?: T;
  error?: unknown;
  response?: { status: number };
};

async function exec<T>(fn: Promise<SdkResult<T>>): Promise<T> {
  const { data, error, response } = await fn;
  if (error) {
    if (response?.status && typeof error === "object" && error !== null) {
      (error as any).status = response.status;
    }
    throw error;
  }
  if (data === undefined) throw new Error("Empty response");
  return data;
}

// Auth

/**
 * Create a clinician account. Enumeration-safe: an email that is already
 * registered returns the same shape, so the caller cannot probe for accounts.
 *
 * Registration emails the first sign-in code and returns its `challenge_id` —
 * verify *that* challenge rather than calling `sendMfaCode()`, which inside the
 * resend cooldown is a 429.
 */
export async function registerAccount(
  email: string,
  fullName: string,
  password: string
): Promise<RegisterResponse> {
  return exec(
    postAuthRegister({
      client: authedClient,
      body: { email, full_name: fullName, password },
    })
  );
}

/**
 * Which second factor, if any, is outstanding for the caller.
 *
 * Runs on a *pre-MFA* token (password sign-in has happened, the gate has not been
 * cleared), so it still needs auth headers — unlike the old magic-link wrappers,
 * which were fully unauthenticated.
 */
export async function getMfaStatus(): Promise<MfaStatusResponse> {
  return exec(
    getAuthMfaStatus({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

/**
 * Email a fresh sign-in code to the authenticated (pre-MFA) caller.
 *
 * Returns `method: "totp"` with no `challenge_id` when the account uses an
 * authenticator instead — nothing is emailed in that case.
 */
export async function sendMfaCode(): Promise<MfaSendResponse> {
  return exec(
    postAuthMfaSend({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

/**
 * Clear the second factor and open the 30-day trusted-device window.
 *
 * Pass `challengeId` for an emailed code; omit it for a TOTP code. The caller
 * MUST `getIdToken(true)` afterwards — the claims are written to the Firebase
 * user, so a token already in hand never gains them and every request keeps
 * failing with `MFA_REQUIRED`.
 */
export async function verifyMfaCode(
  code: string,
  challengeId?: string
): Promise<MfaVerifyResponse> {
  return exec(
    postAuthMfaVerify({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { code, challenge_id: challengeId },
    })
  );
}

/** Start a password reset. Always succeeds, whether or not the email exists. */
export async function requestPasswordReset(email: string): Promise<void> {
  await exec(
    postAuthPasswordForgot({
      client: authedClient,
      body: { email },
    })
  );
}

/**
 * Complete a password reset. Revokes existing sessions and clears the MFA window.
 *
 * Takes the email, not a challenge id: `/auth/password/forgot` deliberately
 * returns no id (that would confirm the account exists), so the server resolves
 * the in-flight challenge from the address.
 */
export async function resetPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await exec(
    postAuthPasswordReset({
      client: authedClient,
      body: { email, code, new_password: newPassword },
    })
  );
}

export async function getTotpStatus(): Promise<TotpStatusResponse> {
  return exec(
    getUserTotpStatus({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function setupTotp(): Promise<SetupTotpResponse> {
  return exec(
    postUserSetupTotp({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function confirmTotpSetup(code: string): Promise<void> {
  await exec(
    postUserConfirmTotpSetup({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { code },
    })
  );
}

export async function disableTotp(code: string): Promise<void> {
  await exec(
    postUserDisableTotp({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { code },
    })
  );
}

export async function verifyTotpCode(code: string): Promise<void> {
  await exec(
    postUserVerifyTotp({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { code },
    })
  );
}

export async function getCurrentAuthUser(): Promise<AuthMeResponse> {
  return exec(
    getAuthMe({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  ) as Promise<AuthMeResponse>;
}

// NOTE: patient bind (`POST /auth/patient/bind`) is intentionally NOT wrapped
// here — patient auth/binding lives entirely in the separate portal SPA
// (apps/portal). The clinician app never binds patients.

export type {
  AttachmentInfo,
  MessageDoc,
  OrgInvitation,
  OrgListEntry,
  OrgMemberWithEmail,
  SetupTotpResponse,
  ThreadDetailResponse,
  ThreadDoc,
  ThreadsListResponse,
  TotpStatusResponse,
} from "@/client";

export async function listOrgs(): Promise<OrgListEntry[]> {
  const data = await exec(
    getOrgList({
      client: authedClient,
      headers: await withAuthHeaders(),
    }) as any
  );
  return (data as any).orgs ?? [];
}

/**
 * Persist the caller's active org into their Firebase custom claims. The
 * X-Org-Id header (built from the org store) selects the org. Returns 204 with
 * no body, so we don't go through `exec` (which expects a payload).
 */
export async function setActiveOrg(): Promise<void> {
  const { error } = await postOrgActive({
    client: authedClient,
    headers: await withAuthHeaders(),
  });
  if (error) throw error;
}

export async function createOrganisation(
  name: string
): Promise<CreateOrgResponse> {
  return exec(
    postOrg({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { name },
    }) as any
  ) as Promise<CreateOrgResponse>;
}

export async function deleteOrganisation(): Promise<void> {
  await exec(
    deleteOrg({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function updateOrganisationName(name: string): Promise<void> {
  await exec(
    patchOrgName({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { name },
    })
  );
}

export async function getOrganisationMembers(): Promise<OrgMemberWithEmail[]> {
  const data = await exec(
    getOrgMembers({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
  return (data as any).members ?? [];
}

export async function getOrganisationMember(userId: string): Promise<any> {
  return exec(
    getOrgMembersByUserId({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}`,
    } as any)
  );
}

export async function deleteOrganisationMember(userId: string): Promise<void> {
  await exec(
    deleteOrgMembersByUserId({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}`,
    } as any)
  );
}

export async function updateOrganisationMemberRole(
  userId: string,
  role: string
): Promise<void> {
  await exec(
    patchOrgMembersByUserIdRole({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/members/${encodeURIComponent(userId)}/role`,
      body: { role },
    } as any)
  );
}

export async function getOrganisationInvites(): Promise<OrgInvitation[]> {
  const data = await exec(
    getOrgInvites({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
  return (data as any).invites ?? [];
}

export async function createOrganisationInvite(
  email: string,
  role: string
): Promise<void> {
  await exec(
    postOrgInvites({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { email, role: role as any },
    })
  );
}

export async function deleteOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    deleteOrgInvitesByInviteId({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}`,
    } as any)
  );
}

export async function acceptOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    postOrgInvitesByInviteIdAccept({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/accept`,
    } as any)
  );
}

export async function rejectOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    postOrgInvitesByInviteIdReject({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/reject`,
    } as any)
  );
}

export async function resendOrganisationInvite(
  inviteId: string
): Promise<void> {
  await exec(
    postOrgInvitesByInviteIdResend({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/org/invites/${encodeURIComponent(inviteId)}/resend`,
    } as any)
  );
}

export async function deleteCurrentAccountData(): Promise<void> {
  await exec(
    deleteAccountData({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

// Clients
export async function getClient(clientId: string) {
  return exec(
    getClientGet({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: { client_id: clientId },
    })
  );
}

export async function listClients(): Promise<ClientSummary[]> {
  const data = await exec(
    getClientList({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
  return data.clients;
}

export async function createClient(body: CreateClientRequest) {
  const data = await exec(
    postClientCreate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
  return (data as any).id ?? data;
}

export async function updateClient(body: UpdateClientRequest) {
  await exec(
    postClientUpdate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export async function checkUserOwnsClient(clientId: string) {
  return exec(
    postClientCheckOwnership({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { clientId } as any,
    })
  );
}

export async function sendPortalInvite(
  clientId: string
): Promise<{ email_sent: boolean; token: string }> {
  return exec(
    postClientPortalInvite({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId },
    })
  );
}

export async function grantClientAccess(
  clientId: string,
  userId: string,
  role: string
): Promise<void> {
  await exec(
    postClientAccessGrant({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId, user_id: userId, role } as any,
    })
  );
}

export async function revokeClientAccess(
  clientId: string,
  userId: string
): Promise<void> {
  await exec(
    postClientAccessRevoke({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId, user_id: userId } as any,
    })
  );
}

export async function listClientAccess(clientId: string): Promise<any[]> {
  const data = await exec(
    getClientAccessList({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: { client_id: clientId } as any,
    } as any)
  );
  return (data as any).entries ?? [];
}

export async function requestClientConsent(
  clientId: string
): Promise<{ token: string }> {
  return exec(
    postClientConsentRequest({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId },
    })
  ) as Promise<{ token: string }>;
}

export async function getClientConsent(token: string) {
  return exec(
    getClientConsentGet({
      client: authedClient,
      query: { token },
    })
  );
}

export async function submitClientConsent(
  token: string,
  recording: boolean,
  ai: boolean
): Promise<{ success: boolean }> {
  return exec(
    postClientConsentSubmit({
      client: authedClient,
      body: {
        token,
        has_consented_to_recording: recording,
        has_consented_to_ai: ai,
      },
    })
  ) as Promise<{ success: boolean }>;
}

// Stripe / Billing
// Subscription Flow
export async function createSetupIntent(price_id: string, return_url: string) {
  return exec(
    postStripeCreateSubscriptionIntent({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { price_id, return_url },
    })
  );
}

export async function confirmSubscription(
  price_id: string,
  return_url: string,
  payment_method_id: string
) {
  return exec(
    postStripeConfirmSubscription({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { price_id, return_url, payment_method_id },
    })
  );
}

export async function getCustomerPortal(return_url: string) {
  return exec(
    postStripeCustomerPortal({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { return_url },
    })
  );
}

export async function listUserInvoices(
  starting_after?: string
): Promise<InvoicesResponse> {
  const data = await exec(
    getStripeInvoices({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: { starting_after },
    }) as Promise<SdkResult<InvoicesResponse>>
  );
  return data as InvoicesResponse;
}

export async function checkSubscriptionStatus() {
  return exec(
    getStripeCheckSubscriptionStatus({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function checkSubscriptionStatusInStripe() {
  return exec(
    getStripeCheckSubscriptionStatus({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: { force_refresh: true },
    })
  );
}

/** The org's usage against its allowance for the current billing period. */
export async function getUsageSummary() {
  return exec(
    getUsageSummarySdk({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function revokeAllUserSessions() {
  await exec(
    postAuthRevokeSessions({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function createSessionEvent(params: {
  client_id: string;
  notes?: string | null;
  transcription?: string | null;
  template_id?: string | null;
  title?: string;
}) {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  const body = {
    type: "session",
    user_id: u.uid,
    template_id: params.template_id || "",
    ...params,
  } as any;
  const data = await exec(
    postEventsCreate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
  return (data as any).id ?? data;
}

// Create a document event (replacement for createClientDocument which used a legacy endpoint)
export async function createDocumentEvent(params: {
  client_id: string;
  template_id: string;
  text: string; // serialized editor state (compressed or raw per convention)
  session_ids?: string[];
  context?: string | null;
  title?: string;
}) {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  const body = { type: "document", user_id: u.uid, ...params } as any;
  const data = await exec(
    postEventsCreate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
  return (data as any).id ?? data;
}

export async function autoSaveEventText(params: {
  event_id: string;
  client_id: string;
  content: string;
}) {
  return exec(
    postEventsAutosave({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function commitEventVersion(params: {
  event_id: string;
  client_id: string;
}) {
  return exec(
    postEventsCommitVersion({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function updateEventTemplate(params: {
  event_id: string;
  client_id: string;
  template_id: string;
}) {
  return exec(
    postEventsUpdateTemplate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function updateEventReferences(params: {
  event_id: string;
  client_id: string;
  session_ids: string[];
}) {
  return exec(
    postEventsUpdateReferences({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function updateEventTitle(params: {
  event_id: string;
  client_id: string;
  title: string;
}) {
  return exec(
    postEventsUpdateTitle({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function listEventVersions(params: {
  client_id: string;
  event_id: string;
}) {
  return exec(
    getEventsByClientIdByEventIdVersions({
      client: authedClient,
      headers: await withAuthHeaders(),
      path: {
        client_id: params.client_id,
        event_id: params.event_id,
      },
    } as any)
  );
}

export async function deleteEventVersion(params: {
  client_id: string;
  event_id: string;
  version_id: string;
}) {
  return exec(
    deleteEventByClientIdByEventIdVersionsByVersionId({
      client: authedClient,
      headers: await withAuthHeaders(),
      path: {
        client_id: params.client_id,
        event_id: params.event_id,
        version_id: params.version_id,
      },
    } as any)
  );
}

export async function getEvent(clientId: string, eventId: string) {
  return exec(
    getEventsByClientIdByEventId({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/events/${encodeURIComponent(clientId)}/${encodeURIComponent(eventId)}`,
    } as any)
  );
}

export async function fetchEventsBatch(clientId: string, eventIds: string[]) {
  return exec(
    postEventsBatch({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId, event_ids: eventIds },
    })
  );
}

// Delete an event (session or document). Backend requires eventId in path and clientId as query param.
export async function deleteEvent(params: {
  event_id: string;
  client_id: string;
}) {
  const { event_id, client_id } = params;
  return exec(
    deleteEventByEventId({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: { client_id },
      url: `/event/${encodeURIComponent(event_id)}`,
    } as any)
  );
}

export async function triggerAiSummary(params: {
  client_id: string;
  event_id: string;
}): Promise<{ status: string }> {
  return exec(
    postSessionTriggerAiSummary({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params,
    })
  ) as Promise<{ status: string }>;
}

// User Settings
export async function getUser() {
  return exec(
    getUserApi({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
}

export async function updateUser(body: UpdateUserRequest) {
  return exec(
    patchUser({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export async function resetPdfPreferences(): Promise<User> {
  return exec(
    authedClient.post({
      url: "/user/preferences/reset-pdf",
      headers: await withAuthHeaders(),
    })
  ) as Promise<User>;
}

export type {
  ConfirmEmailChangeBody,
  RequestEmailChangeBody,
  RequestEmailChangeResponse,
  UserInviteItem,
};

export async function requestEmailChange(
  body: RequestEmailChangeBody
): Promise<RequestEmailChangeResponse> {
  return exec(
    postUserRequestEmailChange({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export async function confirmEmailChange(
  body: ConfirmEmailChangeBody
): Promise<User> {
  return exec(
    postUserConfirmEmailChange({
      client: authedClient,
      headers: await withAuthHeaders(),
      body,
    })
  ) as Promise<User>;
}

export async function getUserInvites(): Promise<UserInviteItem[]> {
  const data = (await exec(
    getUserInvitesApi({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  )) as UserInviteListResponse;
  return data.invites;
}

// ========================= Transcription =========================
export async function startTranscription(params: {
  client_id: string;
  event_id: string;
  audio_url: string;
  length_seconds?: number | null;
}) {
  return exec(
    postTranscriptionStart({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function completeTranscriptionMock(params: {
  client_id: string;
  event_id: string;
  transcription: string;
}) {
  return exec(
    postTranscriptionMockComplete({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params,
    })
  );
}

export async function uploadTranscription(params: {
  client_id: string;
  event_id: string;
  transcription: string;
}) {
  return exec(
    postTranscriptionUpload({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params,
    })
  );
}
export async function patchTranscriptionText(params: {
  client_id: string;
  event_id: string;
  index: number;
  text: string;
}) {
  return exec(
    patchTranscriptionTextApi({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params,
    })
  );
}

export async function patchTranscriptionSpeakerLabel(params: {
  client_id: string;
  event_id: string;
  speaker_id: string;
  label: string;
}) {
  return exec(
    patchTranscriptionSpeakerLabelApi({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params,
    })
  );
}

export async function healthCheck() {
  return exec(
    getHealth({
      client: authedClient,
    })
  );
}

// ========================= Templates =========================

export async function listTemplates() {
  const data = await exec(
    getTemplatesList({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  );
  return (data as any).templates ?? data;
}

export async function createTemplate(params: CreateTemplateRequest) {
  return exec(
    postTemplatesCreate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: params as any,
    })
  );
}

export async function getTemplate(templateId: string) {
  return exec(
    getTemplatesByTemplateId({
      client: authedClient,
      headers: await withAuthHeaders(),
      // Generated helper leaves placeholder; supply explicit url
      url: `/templates/${encodeURIComponent(templateId)}`,
    } as any)
  );
}

export async function deleteTemplate(templateId: string) {
  return exec(
    deleteTemplatesByTemplateId({
      client: authedClient,
      headers: await withAuthHeaders(),
      url: `/templates/${encodeURIComponent(templateId)}`,
    } as any)
  );
}

export async function updateTemplateMeta(params: UpdateTemplateMetaRequest) {
  const { template_id, name, description, category, fields } = params;
  return exec(
    patchTemplatesByTemplateId({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { template_id, name, description, category, fields } as any,
      url: `/templates/${encodeURIComponent(template_id!)}`,
    } as any)
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Support Thread API
// ────────────────────────────────────────────────────────────────────────────

export interface CreateThreadResponse {
  thread_id: string;
  status: string;
  created_at: number;
}

/**
 * Declared locally rather than imported from `@/client`: `POST /support/threads`
 * is `multipart/form-data` so it can carry attachments, which keeps it out of
 * the generated OpenAPI SDK.
 */
export interface CreateThreadRequest {
  subject: string;
  category: string;
  message: string;
  attachments?: File[];
}

export async function createThread(
  data: CreateThreadRequest
): Promise<CreateThreadResponse> {
  const form = new FormData();
  form.append("subject", data.subject);
  form.append("category", data.category);
  form.append("message", data.message);
  for (const file of data.attachments ?? []) {
    form.append("attachments", file);
  }
  return exec(
    authedClient.post({
      url: "/support/threads",
      body: form,
      bodySerializer: (v) => v,
      headers: { ...(await withAuthHeaders()), "Content-Type": null },
    })
  ) as Promise<CreateThreadResponse>;
}

export async function listThreads(params?: {
  limit?: number;
  after?: string;
  status?: string;
}): Promise<ThreadsListResponse> {
  return exec(
    getSupportThreads({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: {
        limit: params?.limit,
        after: params?.after,
        status: params?.status,
      },
    })
  ) as Promise<ThreadsListResponse>;
}

export async function getThread(
  threadId: string,
  params?: { limit?: number; after?: string }
): Promise<ThreadDetailResponse> {
  return exec(
    getSupportThreadsByThreadId({
      client: authedClient,
      headers: await withAuthHeaders(),
      path: { thread_id: threadId } as any,
      query: {
        limit: params?.limit,
        after: params?.after,
      },
    } as any)
  ) as Promise<ThreadDetailResponse>;
}

export async function addMessage(
  threadId: string,
  data: { message?: string; attachments?: File[] }
): Promise<MessageDoc> {
  const form = new FormData();
  if (data.message) form.append("message", data.message);
  if (data.attachments) {
    for (const file of data.attachments) {
      form.append("attachments", file);
    }
  }
  return exec(
    authedClient.post({
      url: `/support/threads/${threadId}/messages`,
      body: form,
      bodySerializer: (v) => v,
      headers: { ...(await withAuthHeaders()), "Content-Type": null },
    })
  ) as Promise<MessageDoc>;
}

/**
 * Delete one attachment from a message the caller authored. `gcsPath` is an
 * optional staleness guard: attachments are addressed positionally, so if the
 * path at `index` no longer matches what the client rendered, the server
 * answers 409 rather than deleting the wrong file.
 */
export async function deleteSupportAttachment(
  threadId: string,
  messageId: string,
  index: number,
  gcsPath?: string
): Promise<MessageDoc> {
  return exec(
    authedClient.delete({
      url: `/support/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(
        messageId
      )}/attachments/${index}`,
      query: gcsPath ? { gcs_path: gcsPath } : undefined,
      headers: await withAuthHeaders(),
    })
  ) as Promise<MessageDoc>;
}

export async function closeThread(threadId: string): Promise<void> {
  await exec(
    postSupportThreadsByThreadIdClose({
      client: authedClient,
      headers: await withAuthHeaders(),
      path: { thread_id: threadId } as any,
      url: `/support/threads/${encodeURIComponent(threadId)}/close`,
    } as any)
  );
}

export async function deleteTranscription(params: {
  client_id: string;
  event_id: string;
}) {
  return exec(
    authedClient.post({
      url: "/transcription/delete",
      body: params,
      headers: await withAuthHeaders(),
    })
  );
}

export async function getTranscription(params: {
  client_id: string;
  event_id: string;
}) {
  return exec(
    getTranscriptionGet({
      client: authedClient,
      headers: await withAuthHeaders(),
      query: params,
    } as any)
  );
}

// ========================= Client Transfers =========================

export async function initiateClientTransfer(
  clientId: string,
  targetOrgId: string
): Promise<ClientTransferItem> {
  return exec(
    postClientTransfersInitiate({
      client: authedClient,
      headers: await withAuthHeaders(),
      body: { client_id: clientId, target_org_id: targetOrgId },
    })
  ) as Promise<ClientTransferItem>;
}

export async function listOutgoingTransfers(): Promise<ClientTransferItem[]> {
  const data = (await exec(
    getClientTransfersOutgoing({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  )) as TransferListResponse;
  return data.transfers;
}

export async function listIncomingTransfers(): Promise<ClientTransferItem[]> {
  const data = (await exec(
    getClientTransfersIncoming({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  )) as TransferListResponse;
  return data.transfers;
}

export async function cancelClientTransfer(transferId: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/client/transfers/${encodeURIComponent(transferId)}/cancel`,
      headers: await withAuthHeaders(),
    })
  );
}

export async function acceptClientTransfer(
  transferId: string
): Promise<ClientTransferItem> {
  return exec(
    authedClient.post({
      url: `/client/transfers/${encodeURIComponent(transferId)}/accept`,
      headers: await withAuthHeaders(),
    })
  ) as Promise<ClientTransferItem>;
}

export async function declineClientTransfer(transferId: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/client/transfers/${encodeURIComponent(transferId)}/decline`,
      headers: await withAuthHeaders(),
    })
  );
}

export async function getUserIncomingTransfers(): Promise<
  ClientTransferItem[]
> {
  const data = (await exec(
    getUserTransfersApi({
      client: authedClient,
      headers: await withAuthHeaders(),
    })
  )) as UserTransferListResponse;
  return data.transfers;
}

// Mobile recording

export async function createMobileRecordingSession(params: {
  client_id: string;
  event_id: string;
}): Promise<{ token: string }> {
  return exec(
    authedClient.post({
      url: "/auth/create-mobile-recording-session",
      headers: await withAuthHeaders(),
      body: params,
    })
  ) as Promise<{ token: string }>;
}

export async function exchangeMobileRecordingToken(token: string): Promise<{
  custom_token: string;
  client_id: string;
  event_id: string;
  org_id: string;
}> {
  return exec(
    authedClient.post({
      url: "/auth/exchange-mobile-recording-token",
      body: { token },
    })
  ) as Promise<{
    custom_token: string;
    client_id: string;
    event_id: string;
    org_id: string;
  }>;
}

// Assignments (generic client-assignment engine: assessments, guides, consent)
//
// NOTE: these wrappers call the API by raw URL (matching the established pattern in
// this file) so they stand alone; the generated `types.gen.ts` mirror is refreshed
// with `bun run openapi-ts` when the backend spec changes.

export type AssignmentKind = "assessment" | "guide" | "consent" | "exercise";
export type AssignmentStatus =
  | "assigned"
  | "submitted"
  | "completed"
  | "reviewed"
  | "acknowledged"
  // Terminal soft-delete state. Server-filtered out of all lists, so the client
  // never actually receives one — declared for exhaustiveness.
  | "withdrawn";

/** A single questionnaire item to render. */
export interface AssignmentSchemaItem {
  key: string;
  text: string;
}

/** A shared Likert response option. */
export interface AssignmentSchemaOption {
  label: string;
  value: number;
}

/** The `schema` JSON on an assessment def (guides/consent use `instructions`/free text). */
export interface AssignmentSchema {
  instructions?: string;
  options?: AssignmentSchemaOption[];
  items?: AssignmentSchemaItem[];
  // Guides/consent may store arbitrary body fields; keep it open.
  [key: string]: unknown;
}

export interface AssignmentDef {
  id: string;
  org_id?: string | null;
  kind: AssignmentKind;
  slug: string;
  title: string;
  description?: string | null;
  category?: string | null;
  scorer_key?: string | null;
  schema: AssignmentSchema;
  is_active: boolean;
  created_at: number;
  updated_at: number;
}

export interface AssignmentSubscale {
  name: string;
  value: number;
}

export interface AssignmentScore {
  total: number;
  subscales: AssignmentSubscale[];
  severity: string;
  interpretation: string;
}

export interface Assignment {
  id: string;
  client_id: string;
  def_id: string;
  kind: AssignmentKind;
  title: string;
  status: AssignmentStatus;
  response?: Record<string, number> | null;
  score?: AssignmentScore | null;
  /** How the response was captured: "patient" (self-reported) or "clinician"
   * (administered in session). Null until a response is submitted. */
  source?: "patient" | "clinician" | null;
  assigned_at: number;
  completed_at?: number | null;
  reviewed_at?: number | null;
}

/** Patient-portal shape: an assignment plus its def schema for rendering. */
export interface AssignmentWithDef extends Assignment {
  def: AssignmentDef;
}

// ─── Clinician: library ────────────────────────────────────────────────────────

export async function listAssignmentDefs(
  kind?: AssignmentKind
): Promise<AssignmentDef[]> {
  return exec(
    authedClient.get({
      url: "/assignments/defs",
      headers: await withAuthHeaders(),
      query: kind ? { kind } : undefined,
    })
  ) as Promise<AssignmentDef[]>;
}

export async function createAssignmentDef(body: {
  kind: AssignmentKind;
  slug: string;
  title: string;
  description?: string | null;
  category?: string | null;
  schema: AssignmentSchema;
}): Promise<{ id: string }> {
  return exec(
    authedClient.post({
      url: "/assignments/defs",
      headers: await withAuthHeaders(),
      body,
    })
  ) as Promise<{ id: string }>;
}

export async function updateAssignmentDef(
  defId: string,
  body: {
    title?: string;
    description?: string | null;
    category?: string | null;
    schema?: AssignmentSchema;
  }
): Promise<void> {
  await exec(
    authedClient.patch({
      url: `/assignments/defs/${defId}`,
      headers: await withAuthHeaders(),
      body,
    })
  );
}

export async function setAssignmentDefActive(
  defId: string,
  isActive: boolean
): Promise<void> {
  await exec(
    authedClient.post({
      url: `/assignments/defs/${defId}/active`,
      headers: await withAuthHeaders(),
      body: { is_active: isActive },
    })
  );
}

// ─── Clinician: assignments ────────────────────────────────────────────────────

export async function listClientAssignments(
  clientId: string
): Promise<Assignment[]> {
  return exec(
    authedClient.get({
      url: "/assignments/list",
      headers: await withAuthHeaders(),
      query: { client_id: clientId },
    })
  ) as Promise<Assignment[]>;
}

export async function assign(body: {
  client_id: string;
  def_id: string;
}): Promise<{ id: string }> {
  return exec(
    authedClient.post({
      url: "/assignments/assign",
      headers: await withAuthHeaders(),
      body,
    })
  ) as Promise<{ id: string }>;
}

export async function reviewAssignment(id: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/assignments/${id}/review`,
      headers: await withAuthHeaders(),
    })
  );
}

/** Clinician records an assessment they administered in session. Scores + persists
 * the response, auto-advances the assignment to `reviewed`, and returns the score. */
export async function administerAssessment(
  id: string,
  response: Record<string, number>
): Promise<AssignmentScore> {
  return exec(
    authedClient.post({
      url: `/assignments/${id}/administer`,
      headers: await withAuthHeaders(),
      body: { response },
    })
  ) as Promise<AssignmentScore>;
}

export async function deleteAssignment(id: string): Promise<void> {
  await exec(
    authedClient.delete({
      url: `/assignments/${id}`,
      headers: await withAuthHeaders(),
    })
  );
}

// ─── Patient portal ────────────────────────────────────────────────────────────

export async function getMyAssignments(): Promise<AssignmentWithDef[]> {
  return exec(
    authedClient.get({
      url: "/assignments/mine",
      headers: await withAuthHeaders(),
    })
  ) as Promise<AssignmentWithDef[]>;
}

export async function submitAssessment(
  id: string,
  response: Record<string, number>
): Promise<AssignmentScore> {
  return exec(
    authedClient.post({
      url: `/assignments/${id}/submit`,
      headers: await withAuthHeaders(),
      body: { response },
    })
  ) as Promise<AssignmentScore>;
}

export async function completeGuide(id: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/assignments/${id}/complete`,
      headers: await withAuthHeaders(),
    })
  );
}

export async function acknowledgeConsent(id: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/assignments/${id}/acknowledge`,
      headers: await withAuthHeaders(),
    })
  );
}

// ─── Client-shared documents (clinician side) ──────────────────────────────────
//
// Client-facing notes/letters authored from a parent event, surfaced in the
// patient portal. Wire convention matches events: the client sends **raw Lexical
// JSON** (compressed server-side) and the API returns base64(zlib(Lexical JSON)),
// which we decompress on read. These wrappers hand callers plain Lexical JSON, so
// UI code never touches compression.

import type {
  SharedDocumentDoc as _SharedDocumentDoc,
  PatientSharedDocument as _PatientSharedDocument,
} from "@/client";
import { tryDecompressBase64Zlib } from "@/lib/textCompression";

export type SharedDocumentDoc = _SharedDocumentDoc;
export type PatientSharedDocument = _PatientSharedDocument;

/** Replace a doc's `content` (base64-zlib on the wire) with plain Lexical JSON.
 *  Null/empty/undecodable content becomes "". */
function decompressDocContent<T extends { content?: string | null }>(
  doc: T
): T {
  const raw = doc.content ? tryDecompressBase64Zlib(doc.content) : null;
  return { ...doc, content: raw ?? "" };
}

/** List client-shared documents authored against a parent event (content
 *  decompressed to plain Lexical JSON). */
export async function listSharedDocumentsForEvent(
  eventId: string
): Promise<SharedDocumentDoc[]> {
  const docs = (await exec(
    authedClient.get({
      url: `/shared-documents/event/${encodeURIComponent(eventId)}`,
      headers: await withAuthHeaders(),
    })
  )) as SharedDocumentDoc[];
  return docs.map(decompressDocContent);
}

/** Create an empty client document for a parent event. Returns `{ id }`. */
export async function createSharedDocument(
  eventId: string
): Promise<{ id: string }> {
  return exec(
    authedClient.post({
      url: "/shared-documents/create",
      headers: await withAuthHeaders(),
      body: { event_id: eventId },
    })
  ) as Promise<{ id: string }>;
}

/** Save edited title/content. `content` is plain Lexical JSON — sent raw; the
 *  server compresses it. Omit a field to leave it unchanged. */
export async function updateSharedDocument(
  id: string,
  body: { title?: string; content?: string }
): Promise<void> {
  await exec(
    authedClient.put({
      url: `/shared-documents/${encodeURIComponent(id)}`,
      headers: await withAuthHeaders(),
      body,
    })
  );
}

/** Synchronous AI draft from the parent event. Returns the refreshed doc with
 *  content decompressed to plain Lexical JSON. The caller shows a spinner scoped
 *  to the editor while this resolves. */
export async function generateSharedDocument(
  id: string
): Promise<SharedDocumentDoc> {
  const doc = (await exec(
    authedClient.post({
      url: `/shared-documents/${encodeURIComponent(id)}/generate`,
      headers: await withAuthHeaders(),
    })
  )) as SharedDocumentDoc;
  return decompressDocContent(doc);
}

/** Deliberately share a document with the client. */
export async function shareSharedDocument(id: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/shared-documents/${encodeURIComponent(id)}/share`,
      headers: await withAuthHeaders(),
    })
  );
}

/** Soft-revoke a shared document (re-shareable later). */
export async function unshareSharedDocument(id: string): Promise<void> {
  await exec(
    authedClient.post({
      url: `/shared-documents/${encodeURIComponent(id)}/unshare`,
      headers: await withAuthHeaders(),
    })
  );
}
